/**
 * Beta 04.06.x (11/06/2026, Ricardo P0): Helper centralizado pra DataLayer events
 *
 * Empurra eventos pro GTM-TN42W63J. GTM repassa pro GA4 (G-40TV7PL97W) + HubSpot
 * via configuração de Tags do próprio GTM.
 *
 * Padrão: nome de evento em snake_case + parâmetros tipados.
 *
 * Eventos implementados:
 *   cta_click           — clique em qualquer CTA "Falar com especialista"
 *   nav_click           — clique em item do menu header
 *   onepager_download   — clique no link de download do PDF executivo
 *   form_view           — primeira interação com campo do form "Fale com a aunica"
 *   form_submit         — submit do form (LEAD CONVERSION)
 *   scroll_depth        — atinge 25%, 50%, 75%, 100% da página
 *   section_view        — entra em viewport de seção (Squad, Cases, FAQ, etc)
 *   external_link_click — clique em link externo (LinkedIn, aunica.com, etc)
 *   faq_open            — abre item do FAQ
 *
 * Como configurar GTM: ver /outputs/GTM_TAGUEAMENTO_AVANCADO.md
 */

type DataLayerEvent = Record<string, any> & { event: string };

declare global {
  interface Window {
    dataLayer?: any[];
  }
}

/** Push genérico. SSR-safe (checa window). */
export function dlPush(event: DataLayerEvent): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[dataLayer]', event);
  }
}

// ────────────────────────────────────────────────────────────────────────
// CTA clicks
// ────────────────────────────────────────────────────────────────────────
export function trackCtaClick(params: {
  cta_label: string;
  cta_location: 'header' | 'hero' | 'planos' | 'cases' | 'contato' | 'footer' | 'mobile';
  cta_destination?: string;
}): void {
  dlPush({
    event: 'cta_click',
    ...params,
    cta_category: 'engagement',
  });
}

// ────────────────────────────────────────────────────────────────────────
// Navigation
// ────────────────────────────────────────────────────────────────────────
export function trackNavClick(params: { nav_item: string; nav_target: string }): void {
  dlPush({
    event: 'nav_click',
    ...params,
  });
}

// ────────────────────────────────────────────────────────────────────────
// One-pager download
// ────────────────────────────────────────────────────────────────────────
export function trackOnepagerDownload(params?: { source?: string }): void {
  dlPush({
    event: 'onepager_download',
    file_name: 'ser-ai-insight-one-pager.pdf',
    file_extension: 'pdf',
    link_text: 'Baixar one-pager executivo',
    source: params?.source ?? 'hero',
  });
}

// ────────────────────────────────────────────────────────────────────────
// Form lifecycle (Fale com a aunica)
// ────────────────────────────────────────────────────────────────────────
export function trackFormView(form_name = 'fale_com_aunica'): void {
  dlPush({ event: 'form_view', form_name });
}

export function trackFormStart(form_name = 'fale_com_aunica'): void {
  dlPush({ event: 'form_start', form_name });
}

export function trackFormFieldInteract(params: { form_name?: string; field_name: string }): void {
  dlPush({
    event: 'form_field_interact',
    form_name: params.form_name ?? 'fale_com_aunica',
    field_name: params.field_name,
  });
}

/**
 * EVENTO PRINCIPAL DE LEAD/CONVERSÃO.
 * Marcar como conversion no GA4 + sync HubSpot via API.
 */
export function trackFormSubmit(params: {
  form_name?: string;
  has_company?: boolean;
  has_message?: boolean;
  source?: string;
}): void {
  dlPush({
    event: 'form_submit',
    event_category: 'lead',
    event_label: 'fale_com_aunica',
    form_name: params.form_name ?? 'fale_com_aunica',
    has_company: !!params.has_company,
    has_message: !!params.has_message,
    source: params.source ?? 'landing',
    // GA4 reserva nome 'generate_lead' como conversion event padrão
    // Adicionar segunda push pra disparar conversion no GA4
  });
  dlPush({
    event: 'generate_lead',
    currency: 'BRL',
    value: 1,
    method: 'website_form',
  });
}

export function trackFormError(params: { form_name?: string; error: string }): void {
  dlPush({
    event: 'form_error',
    form_name: params.form_name ?? 'fale_com_aunica',
    error_message: params.error,
  });
}

// ────────────────────────────────────────────────────────────────────────
// Scroll depth
// ────────────────────────────────────────────────────────────────────────
const scrollMilestonesFired = new Set<number>();
export function trackScrollDepth(percent: 25 | 50 | 75 | 100): void {
  if (scrollMilestonesFired.has(percent)) return;
  scrollMilestonesFired.add(percent);
  dlPush({
    event: 'scroll_depth',
    scroll_percent: percent,
  });
}

// ────────────────────────────────────────────────────────────────────────
// Section view (IntersectionObserver)
// ────────────────────────────────────────────────────────────────────────
const sectionViewsFired = new Set<string>();
export function trackSectionView(section_id: string): void {
  if (sectionViewsFired.has(section_id)) return;
  sectionViewsFired.add(section_id);
  dlPush({
    event: 'section_view',
    section_id,
  });
}

// ────────────────────────────────────────────────────────────────────────
// External link
// ────────────────────────────────────────────────────────────────────────
export function trackExternalLink(params: { url: string; link_text?: string }): void {
  dlPush({
    event: 'external_link_click',
    link_url: params.url,
    link_text: params.link_text,
    link_domain: (() => {
      try { return new URL(params.url).hostname; } catch { return ''; }
    })(),
  });
}

// ────────────────────────────────────────────────────────────────────────
// FAQ
// ────────────────────────────────────────────────────────────────────────
export function trackFaqOpen(params: { question: string }): void {
  dlPush({
    event: 'faq_open',
    faq_question: params.question.substring(0, 100),
  });
}

// ────────────────────────────────────────────────────────────────────────
// Setup global: scroll listener + page view enriquecido
// Chamado uma vez no Provider root.
// ────────────────────────────────────────────────────────────────────────
export function initLandingTracking(): void {
  if (typeof window === 'undefined') return;

  // Page view enriquecido com user_type=anonymous + landing_version
  dlPush({
    event: 'landing_view',
    landing_version: '04.06',
    page_path: window.location.pathname,
    page_referrer: document.referrer || 'direct',
  });

  // Scroll depth listener
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      const pct = total > 0 ? Math.min(100, (window.scrollY / total) * 100) : 0;
      if (pct >= 25)  trackScrollDepth(25);
      if (pct >= 50)  trackScrollDepth(50);
      if (pct >= 75)  trackScrollDepth(75);
      if (pct >= 95)  trackScrollDepth(100); // 95 pra dar margem de pixels
      ticking = false;
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });

  // Section view via IntersectionObserver pras seções ancoradas
  const sectionIds = [
    'por-que', 'o-que-e', 'ciclo', 'como-funciona',
    'data-lakes', 'squad', 'autonomo', 'planos',
    'cases', 'faq', 'contato',
  ];
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio > 0.4) {
          trackSectionView(entry.target.id);
        }
      }
    }, { threshold: [0.4] });
    // Aguarda DOM estar pronto pra observar
    setTimeout(() => {
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      }
    }, 1000);
  }
}
